import React, { Fragment } from 'react';
import { Button, Form, Modal, Dimmer, Loader, Table } from 'semantic-ui-react';
import { inject, observer } from 'mobx-react';
import Rx from 'stores/Rx';
import * as styles from './styles';
import { observable, IObservableArray, toJS } from 'mobx';
import type { AssignableUser } from 'stores/Account';
import MessageBox from 'components/MessageBox';
import MsgStore from 'stores/MsgStore';
import UiShared from 'stores/UiShared';
import Auth from 'stores/Auth';
import ItemInfor from 'stores/ItemInfor';
import PrescriberPractice from 'stores/PrescriberPractice';
import Prescriber from 'stores/Prescriber';
import _ from 'lodash';
import API from 'api';
import PatientHeader from 'components/Patient';

@inject(rootStore => ({
	auth: rootStore.auth,
	uiShared: rootStore.uiShared
}))
@observer
export default class ScriptPending extends React.Component {
	props: {
		isOpen: boolean,
		scriptId: number,
		onRequestClose: () => {},
		onRequestModify: () => {},
		auth?: Auth,
		uiShared: UiShared
	};

	static defaultProps = {
		isOpen: false
	};
	uploadInput = React.createRef();

	msgStore: MsgStore = new MsgStore();

	@observable loading: boolean = false;
	@observable showButtons: boolean = false;
	@observable showResponseMsg: boolean = false;
	@observable hideRx: boolean = false;
	@observable showConfirmDlg: boolean = false;
	@observable showDeleteConfirmDlg: boolean = false;
	@observable rx: Rx = null;
	@observable prescribers: IObservableArray<Prescriber> = [];
	@observable practices: IObservableArray<Practice> = [];
	@observable prescriberId: number = -1;
	@observable practiceId: number = -1;
	@observable therapeuticAreas: Array<object> = [];
	@observable therapeuticAreaValue: string = '';
	@observable drugs: Array<object> = [];
	@observable drugValue: string = '';
	@observable indications: Array<object> = [];
	@observable indicationValue: string = '';
	pps: Array<PrescriberPractice> = [];
	iis: Array<object> = [];
	@observable isLoadingPatientSearch: boolean = false;

	componentDidUpdate = async prevProps => {
		if (this.props.scriptId && prevProps.scriptId !== this.props.scriptId) {
			await this.loadRx();
		}
	};

	loadRx = async () => {
		this.loading = true;
		this.rx = await Rx.loadById(this.props.scriptId);
		this.showButtons = true;
		this.loading = false;
	};

	removeDuplicates = (myArr, prop) => {
		return myArr.filter((obj, pos, arr) => {
			return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos;
		});
	};

	renderButtons = () => {
		if (!this.showButtons) return;
		const { auth } = this.props;
		const role = auth.user.role;
		let btn = [];
		btn.push(this.renderUpdate());
		if (role === 'manager') {
			btn.push(this.renderMedicare());
		}
		return btn;
	};

	renderUpdate = () => {
		return (
			<Button primary onClick={() => this.props.onRequestModify()} key="update">
				Modify
			</Button>
		);
	};

	renderMedicare = () => {
		return (
			<Button
				primary
				onClick={() => {
					this.showConfirmDlg = true;
				}}
				className={styles.greenButton}
				key="medicare"
			>
				Request Authority
			</Button>
		);
	};

	renderDelete = () => {
		return (
			<Button
				primary
				onClick={() => {
					this.showDeleteConfirmDlg = true;
				}}
				key="delete"
				className={styles.redButton}
			>
				Delete Rx
			</Button>
		);
	};

	onClose = shouldSubmit => {
		this.props.onRequestClose(shouldSubmit);
	};

	renderQuestions = () => {
		if (!this.rx?.questions) return;
		const q = toJS(this.rx.questions);
		const questions = JSON.parse(q);
		if (questions?.length > 0) {
			const renderedQuestions = questions.map((question, i) => {
				return this.renderQuestion(question, i);
			});
			return (
				<Fragment>
					<h3>Restriction Questions</h3>
					<div>{renderedQuestions}</div>
				</Fragment>
			);
		}
	};

	renderQuestion = (question, key) => {
		let answerText;
		if (question.restrictionAnswerType === 'LIST') {
			const a = _.find(question.restrictionAnswerList?.restrictionAnswer, {
				restrictionAnswerId: question.restrictionQuestionAnswer
			});
			answerText = a?.restrictionAnswerText;
		} else {
			answerText = question.restrictionQuestionAnswer || '';
		}
		return (
			<Form.Field key={key} className={styles.questionCont}>
				<label className={styles.questionLbl}>{question.restrictionQuestionText}</label>
				<Form.Input value={answerText} width={16} readOnly />
			</Form.Field>
		);
	};

	deleteRx = async () => {
		const { uiShared } = this.props;
		try {
			this.msgStore.clear();
			uiShared.showSpinner('Deleting...');
			const rx = await Rx.loadById(this.props.scriptId);
			rx.disabled = true;
			await rx.update();
			await this.props.onRequestReload();
			this.onClose();
		} catch (e) {
			this.msgStore.setError(e);
		} finally {
			uiShared.hideModal();
		}
	};

	render() {
		const { isOpen, auth } = this.props;
		const {
			rx,
			loading,
			showDebug,
			showResponseMsg,
			renderResponseMsg,
			renderDebug,
			onClose
		} = this;

		const rxDate = rx?.rxDate?.toDate().format() || '';
		const patient = rx?.Patient;
		const showButtons = auth?.user?.role !== 'viewer';
		return (
			rx && (
				<Fragment>
					<Modal open={isOpen} size="small" onClose={() => onClose()} closeOnDimmerClick={false}>
						<Fragment>
							<Dimmer active={loading} inverted>
								<Loader content="Loading" />
							</Dimmer>
							<Modal.Content>
								<Form>
									<MessageBox msgStore={this.msgStore} />
									<div className={styles.buttonsRowSpaced}>
										<div className={styles.buttonCont}>{showButtons && this.renderDelete()}</div>
										<div>
											{showButtons && this.renderButtons()}
											<Button onClick={() => onClose()}>Close</Button>
										</div>
									</div>
									{showResponseMsg && renderResponseMsg()}
									{showDebug && renderDebug()}
									<Fragment>
										<PatientHeader patient={patient} />

										<h3>Rx Details</h3>
										<Table
											basic="very"
											compact="very"
											verticalAlign="top"
											padded="false"
											collapsing
										>
											<Table.Body>
												<Table.Row key={1}>
													<Table.Cell width="4">Prescriber:</Table.Cell>
													<Table.Cell>{`${rx.PrescriberPractice.Prescriber.prescriberName} ${
														rx.PrescriberPractice.Prescriber.prescriberSurname
													}`}</Table.Cell>
												</Table.Row>
												<Table.Row key={2}>
													<Table.Cell>Practice:</Table.Cell>
													<Table.Cell>{`${
														rx.PrescriberPractice.Practice.practiceName
													}`}</Table.Cell>
												</Table.Row>
												<Table.Row key={3}>
													<Table.Cell>Authority Number:</Table.Cell>
													<Table.Cell>{rx.authorityNo}</Table.Cell>
												</Table.Row>
												<Table.Row key={4}>
													<Table.Cell>Script Date:</Table.Cell>
													<Table.Cell>{rxDate}</Table.Cell>
												</Table.Row>
												<Table.Row key={5}>
													<Table.Cell>Drug:</Table.Cell>
													<Table.Cell>{rx.ItemInfo.itemName}</Table.Cell>
												</Table.Row>
												<Table.Row key={6}>
													<Table.Cell>Treatment:</Table.Cell>
													<Table.Cell>{rx.treatmentType}</Table.Cell>
												</Table.Row>
												<Table.Row key={7}>
													<Table.Cell>Indication:</Table.Cell>
													<Table.Cell>{rx.ItemInfo?.indication}</Table.Cell>
												</Table.Row>
												<Table.Row key={8}>
													<Table.Cell>Item Code:</Table.Cell>
													<Table.Cell>{rx.ItemInfo?.itemCode}</Table.Cell>
												</Table.Row>
												<Table.Row key={9}>
													<Table.Cell>Restriction Code:</Table.Cell>
													<Table.Cell>{rx.ItemInfo?.restrictionCode}</Table.Cell>
												</Table.Row>
												<Table.Row key={10}>
													<Table.Cell verticalAlign="top">Restriction Text:</Table.Cell>
													<Table.Cell>{rx.ItemInfo?.restrictionText}</Table.Cell>
												</Table.Row>
												<Table.Row key={11}>
													<Table.Cell>Eye:</Table.Cell>
													<Table.Cell>{rx.eye}</Table.Cell>
												</Table.Row>
												<Table.Row key={12}>
													<Table.Cell>Number of Repeats:</Table.Cell>
													<Table.Cell>{rx.noOfRepeats}</Table.Cell>
												</Table.Row>
												<Table.Row key={13}>
													<Table.Cell>Quantity:</Table.Cell>
													<Table.Cell>{rx.quantity}</Table.Cell>
												</Table.Row>
												<Table.Row key={14}>
													<Table.Cell>Dose:</Table.Cell>
													<Table.Cell>{rx.dose}</Table.Cell>
												</Table.Row>
											</Table.Body>
										</Table>

										{this.renderQuestions()}
										<div className={styles.buttonsRowSpaced}>
											<div className={styles.buttonCont}>{showButtons && this.renderDelete()}</div>
											<div>
												{showButtons && this.renderButtons()}
												<Button onClick={() => onClose()}>Close</Button>
											</div>
										</div>
									</Fragment>
								</Form>
							</Modal.Content>
						</Fragment>
					</Modal>
					<Modal
						size="mini"
						open={this.showConfirmDlg}
						onClose={() => (this.showConfirmDlg = false)}
						className={styles.modalConfirmation}
						closeIcon={true}
					>
						<Modal.Content>
							<p>
								I agree all information for this PBS authority application is true and correct. I
								acknowledge that the date on the prescription will become the submission date.
							</p>
						</Modal.Content>
						<Modal.Actions>
							<Button
								positive
								icon="checkmark"
								labelPosition="right"
								content="I agree"
								onClick={() => {
									this.showConfirmDlg = false;
									onClose(true);
								}}
							/>
						</Modal.Actions>
					</Modal>
					<Modal
						size="mini"
						open={this.showDeleteConfirmDlg}
						onClose={() => (this.showDeleteConfirmDlg = false)}
						className={styles.modalConfirmation}
						closeIcon={true}
					>
						<Modal.Content>
							<p>Do you wish to permanently delete the Rx record?</p>
						</Modal.Content>
						<Modal.Actions className={styles.buttonsRowSpaced}>
							<Button
								onClick={() => {
									this.showDeleteConfirmDlg = false;
								}}
							>
								Cancel
							</Button>
							<Button
								positive
								icon="checkmark"
								labelPosition="right"
								content="OK"
								onClick={() => {
									this.showDeleteConfirmDlg = false;
									this.deleteRx();
								}}
							/>
						</Modal.Actions>
					</Modal>
				</Fragment>
			)
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/Script/ScriptPending.js